import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Segment,
  Header,
  Loader,
  Message,
  Image,
  Checkbox,
  Input,
  Dropdown,
} from "semantic-ui-react";
import confetti from "canvas-confetti";
import { getPortraitReviewApplications } from "../../../../../apis/portraitReviewApi";
import { passportsSelectPortrait } from "../../../../../apis/applicantApi";
import { getDateFormat } from "../../../../../common/date-format";
import { getApplicantStatusString } from "../../../../routers/DashboardRouter/routers/Products/constants/applicantStatuses";
import { LocalStorageService } from "../../../../../services/localStorageService";
import toastr from "toastr";

const ITEMS_PER_PAGE = 20;

const DEFAULT_FILTERS = {
  ds11: true,
  ds82: true,
  onlineEligible: false,
  fastTrack: false,
  dateFrom: '',
  dateTo: '',
  sortDirection: 'desc',
};

const STORAGE_KEY = 'portrait-review-filters';

const PortraitReview = () => {
  const validateFilters = useCallback((filters) => {
    const validated = { ...DEFAULT_FILTERS };
    if (!filters || typeof filters !== 'object') return validated;

    if (typeof filters.ds11 === 'boolean') validated.ds11 = filters.ds11;
    if (typeof filters.ds82 === 'boolean') validated.ds82 = filters.ds82;
    if (typeof filters.onlineEligible === 'boolean') validated.onlineEligible = filters.onlineEligible;
    if (typeof filters.fastTrack === 'boolean') validated.fastTrack = filters.fastTrack;
    if (typeof filters.dateFrom === 'string') validated.dateFrom = filters.dateFrom;
    if (typeof filters.dateTo === 'string') validated.dateTo = filters.dateTo;
    if (filters.sortDirection === 'asc' || filters.sortDirection === 'desc') {
      validated.sortDirection = filters.sortDirection;
    }

    return validated;
  }, []);

  const initializeFilters = useCallback(() => {
    const stored = LocalStorageService.getItem(STORAGE_KEY);
    return stored ? validateFilters(stored) : DEFAULT_FILTERS;
  }, [validateFilters]);

  const [selectingId, setSelectingId] = useState(null);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [collapsedRows, setCollapsedRows] = useState({});
  const [confettiShown, setConfettiShown] = useState(false);
  const [filters, setFilters] = useState(initializeFilters);
  const loaderRef = useRef(null);
  const endMessageRef = useRef(null);
  const filtersRef = useRef(filters);

  const triggerConfetti = useCallback(() => {
    const duration = 3000;
    const end = Date.now() + duration;

    const colors = ["#21ba45", "#2185d0", "#fbbd08", "#db2828", "#a333c8"];

    const frame = () => {
      confetti({
        particleCount: 3,
        angle: 60,
        spread: 55,
        origin: { x: 0 },
        colors,
      });
      confetti({
        particleCount: 3,
        angle: 120,
        spread: 55,
        origin: { x: 1 },
        colors,
      });

      if (Date.now() < end) {
        requestAnimationFrame(frame);
      }
    };

    frame();

    setTimeout(() => {
      confetti({
        particleCount: 150,
        spread: 100,
        origin: { y: 0.6 },
        colors,
      });
    }, 500);

    setTimeout(() => {
      confetti({
        particleCount: 100,
        angle: 60,
        spread: 80,
        origin: { x: 0, y: 0.6 },
        colors,
      });
      confetti({
        particleCount: 100,
        angle: 120,
        spread: 80,
        origin: { x: 1, y: 0.6 },
        colors,
      });
    }, 1000);
  }, []);

  const loadItems = useCallback(async (pageNum, append = false, currentFilters = null) => {
    if (append) {
      setIsLoadingMore(true);
    } else {
      setIsLoading(true);
    }

    const activeFilters = currentFilters || filtersRef.current;

    try {
      const response = await getPortraitReviewApplications({
        page: pageNum,
        limit: ITEMS_PER_PAGE,
        ds11: activeFilters.ds11,
        ds82: activeFilters.ds82,
        onlineEligible: activeFilters.onlineEligible,
        fastTrack: activeFilters.fastTrack,
        dateFrom: activeFilters.dateFrom,
        dateTo: activeFilters.dateTo,
        sortDirection: activeFilters.sortDirection,
      });

      const newItems = response.data.data || [];
      const total = response.data.pagination?.total || 0;
      const totalPages = response.data.pagination?.totalPages || 1;

      setTotalItems(total);
      setHasMore(pageNum < totalPages);

      if (append) {
        setItems((prev) => [...prev, ...newItems]);
      } else {
        setItems(newItems);
      }
    } catch (error) {
      console.error("Error loading items:", error);
      toastr.error("Failed to load applications");
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    filtersRef.current = filters;
    LocalStorageService.setItem(STORAGE_KEY, filters);
    setPage(1);
    setConfettiShown(false);
    loadItems(1, false, filters);
  }, [filters, loadItems]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoading && !isLoadingMore) {
          const nextPage = page + 1;
          setPage(nextPage);
          loadItems(nextPage, true);
        }
      },
      { threshold: 0.1 }
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => observer.disconnect();
  }, [hasMore, isLoading, isLoadingMore, page, loadItems]);

  const allItemsReviewed = items.length > 0 && items.every(item => collapsedRows[item._id]);

  useEffect(() => {
    if (hasMore || confettiShown || !allItemsReviewed) return;

    triggerConfetti();
    setConfettiShown(true);
  }, [hasMore, confettiShown, allItemsReviewed, triggerConfetti]);

  const handleSelectPhoto = useCallback(
    async (applicant, portraitId) => {
      const key = `${applicant._id}-${portraitId}`;
      setSelectingId(key);

      try {
        await passportsSelectPortrait(applicant._id, applicant.api, {
          portraitId,
          select: true,
        });

        toastr.success("Photo selected successfully");

        setItems((prev) =>
          prev.map((item) =>
            item._id === applicant._id
              ? { ...item, selectedPicture: portraitId }
              : item
          ),
        );
        setCollapsedRows((prev) => ({ ...prev, [applicant._id]: true }));
      } catch (error) {
        console.error("Error selecting photo:", error);
        toastr.error("Failed to select photo");
      } finally {
        setSelectingId(null);
      }
    },
    [],
  );

  const toggleRowCollapse = useCallback((applicantId) => {
    setCollapsedRows((prev) => ({
      ...prev,
      [applicantId]: !prev[applicantId],
    }));
  }, []);

  const handleFilterChange = useCallback((updates) => {
    setFilters((prev) => ({ ...prev, ...updates }));
  }, []);

  const getSixMonthsAgo = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 6);
    return date.toISOString().split('T')[0];
  };

  const getToday = () => {
    return new Date().toISOString().split('T')[0];
  };

  const sortOptions = [
    { key: 'desc', text: 'Newest first', value: 'desc' },
    { key: 'asc', text: 'Oldest first', value: 'asc' },
  ];

  const renderPortraits = useCallback(
    (applicant) => {
      const pictures = Array.isArray(applicant?.portraitPictures)
        ? applicant.portraitPictures
        : [];

      if (pictures.length === 0) {
        return (
          <div
            style={{
              padding: "1rem",
              backgroundColor: "#f5f5f5",
              borderRadius: "4px",
              color: "#888",
              fontSize: "14px",
            }}
          >
            No portrait pictures available
          </div>
        );
      }

      return (
        <div style={{ display: "flex", gap: "12px", flexWrap: "wrap" }}>
          {pictures.map((portrait) => {
            const isSelecting =
              selectingId === `${applicant._id}-${portrait.id}`;
            return (
              <div
                key={portrait.id}
                style={{
                  position: "relative",
                  cursor: isSelecting ? "wait" : "pointer",
                  opacity: isSelecting ? 0.5 : 1,
                  border:
                    applicant.selectedPicture === portrait.id
                      ? "4px solid green"
                      : "2px solid #ccc",
                  borderRadius: "4px",
                  padding: "2px",
                }}
                onClick={() =>
                  !isSelecting && handleSelectPhoto(applicant, portrait.id)
                }
                title={`Click to select - Uploaded: ${getDateFormat(portrait.uploadedAt)}`}
              >
                {portrait.url ? (
                  <Image
                    src={portrait.url}
                    style={{ width: "150px", height: "150px", objectFit: "cover" }}
                  />
                ) : (
                  <div
                    style={{
                      width: "150px",
                      height: "150px",
                      backgroundColor: "#eee",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "12px",
                    }}
                  >
                    No URL
                  </div>
                )}
                {isSelecting && (
                  <Loader
                    active
                    size="medium"
                    style={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      transform: "translate(-50%, -50%)",
                    }}
                  />
                )}
              </div>
            );
          })}
        </div>
      );
    },
    [selectingId, handleSelectPhoto],
  );

  return (
    <React.Fragment>
      <Segment.Group style={{ display: "table", width: "100%" }}>
        <Segment className="tableTitle adminTable" inverted>
          <Header as="h1" inverted style={{ margin: 0 }}>U.S. Passport Portrait Review</Header>
        </Segment>

        <Segment>
          <div style={{ display: "flex", gap: "1rem", alignItems: "center", flexWrap: "wrap", marginBottom: "1rem" }}>
            <Checkbox
              label="DS-11"
              checked={filters.ds11}
              onChange={(e, { checked }) => handleFilterChange({ ds11: checked })}
              disabled={filters.onlineEligible}
            />
            <Checkbox
              label="DS-82"
              checked={filters.ds82}
              onChange={(e, { checked }) => handleFilterChange({ ds82: checked })}
              disabled={filters.onlineEligible}
            />
            <Checkbox
              label="Online Eligible"
              checked={filters.onlineEligible}
              onChange={(e, { checked }) => handleFilterChange({
                onlineEligible: checked,
                ...(checked ? { ds11: false, ds82: true } : {})
              })}
            />
            <Checkbox
              label="Fast Track"
              checked={filters.fastTrack}
              onChange={(e, { checked }) => handleFilterChange({ fastTrack: checked })}
            />
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <label>From:</label>
              <Input
                type="date"
                value={filters.dateFrom}
                onChange={(e, { value }) => handleFilterChange({ dateFrom: value })}
                min={getSixMonthsAgo()}
                max={filters.dateTo || getToday()}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <label>To:</label>
              <Input
                type="date"
                value={filters.dateTo}
                onChange={(e, { value }) => handleFilterChange({ dateTo: value })}
                min={filters.dateFrom || getSixMonthsAgo()}
                max={getToday()}
              />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
              <label>Sort:</label>
              <Dropdown
                selection
                options={sortOptions}
                value={filters.sortDirection}
                onChange={(e, { value }) => handleFilterChange({ sortDirection: value })}
              />
            </div>
          </div>
        </Segment>

        {isLoading && items.length === 0 ? (
          <Segment>
            <Loader active />
          </Segment>
        ) : (
          <Segment>
            {items.length === 0 ? (
              <Message>
                No applications pending portrait review.
              </Message>
            ) : (
              <>
                <div style={{ marginBottom: "1rem" }}>
                  <strong>Total Records: </strong> {totalItems}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
                  {items.map((applicant) => {
                    const isCollapsed = collapsedRows[applicant._id];
                    return (
                      <div
                        key={applicant._id}
                        style={{
                          border: isCollapsed ? "1px solid #21ba45" : "1px solid #ddd",
                          borderRadius: "4px",
                          overflow: "hidden",
                        }}
                      >
                        <div
                          style={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "1rem",
                            padding: "0.75rem 1rem",
                            backgroundColor: isCollapsed ? "#e8f5e9" : "#f9fafb",
                            borderBottom: isCollapsed ? "none" : "1px solid #ddd",
                            alignItems: "center",
                            cursor: isCollapsed ? "pointer" : "default",
                          }}
                          onClick={() => isCollapsed && toggleRowCollapse(applicant._id)}
                          title={isCollapsed ? "Click to expand and change selection" : ""}
                        >
                          {isCollapsed && (
                            <div style={{ color: "#21ba45", fontWeight: "bold" }}>
                              [Done]
                            </div>
                          )}
                          <div>
                            <strong>ID: </strong>
                            <a
                              href={`/${applicant.api}/applicant/${applicant._id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {applicant._id}
                            </a>
                          </div>
                          <div>
                            <strong>Product: </strong>
                            {applicant.api?.toUpperCase()}
                          </div>
                          <div>
                            <strong>Name: </strong>
                            {applicant.firstName} {applicant.lastName}
                          </div>
                          <div>
                            <strong>Email: </strong>
                            {applicant.email}
                          </div>
                          <div>
                            <strong>Status: </strong>
                            {getApplicantStatusString(applicant.status) ||
                              `Status ${applicant.status}`}
                          </div>
                          <div>
                            <strong>Created: </strong>
                            {getDateFormat(applicant.createdAt)}
                          </div>
                          {isCollapsed && (
                            <div style={{ marginLeft: "auto", color: "#888", fontSize: "12px" }}>
                              Click to expand
                            </div>
                          )}
                        </div>
                        {!isCollapsed && (
                          <div style={{ padding: "1rem" }}>
                            {renderPortraits(applicant)}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div
                  ref={loaderRef}
                  style={{
                    padding: "1rem",
                    textAlign: "center",
                  }}
                >
                  {isLoadingMore && <Loader active inline />}
                </div>
                {!hasMore && allItemsReviewed && (
                  <div
                    ref={endMessageRef}
                    style={{
                      padding: "3rem 2rem",
                      textAlign: "center",
                      backgroundColor: "#f0f9ff",
                      borderRadius: "8px",
                      margin: "2rem 0",
                      border: "2px solid #21ba45",
                    }}
                  >
                    <Header as="h2" style={{ color: "#21ba45", marginBottom: "0.5rem" }}>
                      All Caught Up!
                    </Header>
                    <p style={{ color: "#666", fontSize: "1.1rem", margin: 0 }}>
                      You have reviewed all {totalItems} applications pending portrait review.
                    </p>
                  </div>
                )}
              </>
            )}
          </Segment>
        )}
      </Segment.Group>
    </React.Fragment>
  );
};

export default PortraitReview;
